import { throttle, noop, omit } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import asyncThrottle from 'throttle-async';
import cookie from 'js-cookie';

import {
    THROTTLE_CHANGE,
    THROTTLE_RESIZE,
    DEFAULT_LAT,
    DEFAULT_LNG,
    LOCALIZED_STRINGS,
    TEXT_LENGTH_THRESHOLD,
    ADDRESS_COOKIE_KEY,
} from '../../constants';

import { Breakpoints, getWindowWidth } from '../../../grid';
import TakeoverInput, { DropdownPrimitives } from '../../../TakeoverInput';
import { fetchPlaceDetails, fetchPlacePredictions } from '../../utils';
import PoweredByGoogle from './assets/powered_by_google.png';
import PoweredByGoogleHdpi from './assets/powered_by_google_hdpi.png';
import * as primitives from './primitives';
import isZocdocBrand from '../../../utils/isZocdocBrand';

const { Label, Option } = DropdownPrimitives;

const throttledFetchPlacePredictions = asyncThrottle(fetchPlacePredictions, THROTTLE_CHANGE);

export class BaseLocationPicker extends PureComponent {
    lastFetchPromise = null;
    geolocationEnabled = typeof navigator !== 'undefined' && 'geolocation' in navigator;

    static propTypes = {
        // for screenshots and debugging purposes
        __predictions: PropTypes.arrayOf(
            /**
             * NOTE: these are the normalized property names; the google API
             * sends back snake_case identifiers and we do some further
             * processing: /src/LocationPicker/utils/prediction-adapter.js
             */
            PropTypes.shape({
                placeId: PropTypes.string,
                matchedSubstrings: PropTypes.arrayOf(
                    PropTypes.shape({
                        length: PropTypes.number,
                        offset: PropTypes.number,
                    })
                ),
                name: PropTypes.string,
            }),
        ),
        __takeoverInputProps: PropTypes.object,
        __useCurrentLocation: PropTypes.bool,

        biasPredictionsToCoordinates: PropTypes.shape({
            lat: PropTypes.number,
            lng: PropTypes.number,
        }),
        defaultValue: PropTypes.string,
        gaCategory: PropTypes.string, // used for tracking
        onBlur: PropTypes.func,
        onChange: PropTypes.func,
        onClickPrediction: PropTypes.func,
        placeholder: PropTypes.string,
        section: PropTypes.string, // used for tracking
        trackPredictionSelect: PropTypes.func,
        trackTextBoxChange: PropTypes.func,
        trackTextBoxFocus: PropTypes.func,
        updateLocation: PropTypes.func,
        theme: PropTypes.shape({
            directoryId: PropTypes.string,
        }),
    };

    static defaultProps = {
        biasPredictionsToCoordinates: {
            lat: DEFAULT_LAT,
            lng: DEFAULT_LNG,
        },
        defaultValue: '',
        gaCategory: undefined,
        onBlur: noop,
        onChange: noop,
        onClickPrediction: noop,
        placeholder: undefined,
        section: undefined,
        trackPredictionSelect: noop,
        trackTextBoxChange: noop,
        trackTextBoxFocus: noop,
        updateLocation: noop,
        theme: {
            directoryId: '-1',
        },
    };

    constructor(props) {
        super(props);

        this.state = {
            predictions: this.props.__predictions || [],
            useCurrentLocation: this.props.__useCurrentLocation || false,
        };
    }

    async componentDidMount() {
        if (!this.props.defaultValue && this.geolocationEnabled && getWindowWidth() < Breakpoints.small) {
            this.selectUseCurrentLocation();
        }

        window.addEventListener('resize', this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize, true);
    }

    render() {
        return (
            <primitives.Wrapper
                {...omit(this.props, BaseLocationPicker.internalProps)}
                locationIconVisible={this.state.useCurrentLocation}
            >
                <TakeoverInput
                    {...this.props.__takeoverInputProps}
                    data-test="location-picker-input"
                    defaultValue={this.props.defaultValue}
                    onBlur={this.props.onBlur}
                    onChange={this.handleChangeInput}
                    onClear={this.handleClear}
                    onClickOption={this.props.onClickPrediction}
                    onClose={this.handleClose}
                    onFocus={this.handleFocusInput}
                    onSelect={this.handleSelect}
                    placeholder={this.getPlaceholder()}
                    wrappedRef={n => { this.takeoverInput = n; }}
                >
                    {this.renderDropdownOptions()}
                </TakeoverInput>
                {this.maybeRenderLocationIcon()}
            </primitives.Wrapper>
        );
    }

    getPlaceholder() {
        const { placeholder, intl } = this.props;
        return placeholder || intl.formatMessage(LOCALIZED_STRINGS.Placeholder);
    }

    renderDropdownOptions() {
        const options = [];

        if (
            (
                this.geolocationEnabled &&
                !this.state.useCurrentLocation &&
                getWindowWidth() < Breakpoints.small
            ) || this.props.__useCurrentLocation
        ) {
            options.push(
                <Option
                    key="use-current-location"
                    data-test="location-picker-use-current-location"
                    data-use-current-location
                    value={this.props.intl.formatMessage(LOCALIZED_STRINGS.MyCurrentLocation)}
                >
                    <FormattedMessage {...LOCALIZED_STRINGS.MyCurrentLocation} />
                    <primitives.InlineLocationIcon data-test="location-icon-option" />
                </Option>
            );
        }

        this.state.predictions.forEach((prediction, index) => {
            options.push(
                <Option
                    key={index}
                    data-place-id={prediction.placeId}
                    data-test={`location-picker-option-${index}`}
                    value={prediction.name}
                >
                    {buildHighlight(prediction)}
                </Option>
            );
        });

        if (options.length) {
            options.push(
                <Label key="powered-by-google">
                    <primitives.PoweredByGoogleContainer>
                        <img
                            alt="powered by Google"
                            height="18"
                            src={PoweredByGoogle}
                            srcSet={`${PoweredByGoogleHdpi} 2x`}
                            width="144"
                        />
                    </primitives.PoweredByGoogleContainer>
                </Label>
            );
        }

        return options;
    }

    maybeRenderLocationIcon() {
        if (this.state.useCurrentLocation) {
            return (
                <primitives.PositionedLocationIcon
                    data-test="location-icon-textbox"
                />
            );
        }
    }

    handleChange = value => {
        this.props.onChange(value);

        const cookieOptions = isZocdocBrand(this.props.theme.directoryId)
            ? { domain: '.zocdoc.com' }
            : {};

        cookie.set(ADDRESS_COOKIE_KEY, value, cookieOptions);
    }

    handleChangeInput = e => {
        const { value } = e.target;

        if (this.state.useCurrentLocation) {
            this.setState({ useCurrentLocation: false });
        }

        this.handleChange(value);

        this.props.trackTextBoxChange(this.props.gaCategory, this.props.section, value);

        if (value.length < TEXT_LENGTH_THRESHOLD) {
            this.setState(() => ({ predictions: [] }));
        } else {
            // eslint-disable-next-line no-multi-assign
            const promise = this.lastFetchPromise = throttledFetchPlacePredictions(
                value,
                this.props.biasPredictionsToCoordinates
            )
                .then(predictions => {
                    // verify this resolved promise is the last promise we created
                    if (this.lastFetchPromise === promise) {
                        this.setState({ predictions });
                    }
                }, noop);
        }
    };

    handleClear = () => {
        this.setState({
            useCurrentLocation: false,
            predictions: [],
        });

        this.handleChange('');
        this.props.trackTextBoxChange(this.props.gaCategory, this.props.section, '');
    };

    handleClose = () => {
        this.setState({ predictions: [] });

        if (!this.takeoverInput.$input.value && getWindowWidth() < Breakpoints.small && this.geolocationEnabled) {
            this.selectUseCurrentLocation();
        }
    };

    handleFocusInput = () => {
        if (getWindowWidth() < Breakpoints.small) {
            if (this.state.useCurrentLocation) {
                this.setState({ useCurrentLocation: false });
                this.takeoverInput.setValue('');
                this.handleChange('');
            }
        }

        this.props.trackTextBoxFocus(this.props.gaCategory, this.props.section);
    };

    handleResize = throttle(() => {
        if (getWindowWidth() >= Breakpoints.small && this.state.useCurrentLocation) {
            this.setState({ useCurrentLocation: false });
        }
    }, THROTTLE_RESIZE);

    handleSelect = async ({ value, target }) => {
        if (target.getAttribute('data-use-current-location') === 'true') {
            return this.selectUseCurrentLocation();
        }

        this.setState({ useCurrentLocation: false });

        const placeId = target.getAttribute('data-place-id');

        this.handleChange(value);
        this.props.trackPredictionSelect(this.props.gaCategory, this.props.section, value);

        const place = await fetchPlaceDetails(placeId);
        if (place) {
            this.props.updateLocation(place);
        }
    };

    selectUseCurrentLocation() {
        this.setState({ useCurrentLocation: true });
        this.takeoverInput.setValue(this.props.intl.formatMessage(LOCALIZED_STRINGS.MyCurrentLocation));

        this.handleChange('');
    }
}

BaseLocationPicker.internalProps = Object.keys(BaseLocationPicker.defaultProps);

/**
 * Builds html with highlighting for a google prediction
 */
export function buildHighlight(prediction) {
    const highlight = [];
    let cursor = 0;

    const { name } = prediction;

    prediction.matchedSubstrings.forEach(match => {
        // characters in front
        if (cursor < match.offset) {
            highlight.push(name.slice(cursor, match.offset));
            cursor = match.offset;
        }

        // highlighted characters
        highlight.push(<mark key={cursor}>{name.slice(cursor, match.offset + match.length)}</mark>);
        cursor += match.length;
    });

    highlight.push(name.slice(cursor)); // remaining characters

    return highlight;
}

export const LocationPicker = injectIntl(BaseLocationPicker);
